import firebase from "config/firebase";
import { toast } from "react-toastify";
import { refreshAuth } from "./authActions";

const storage = firebase.storage();
const uploadFile = async (file, folder) => {
  if (file instanceof File) {
    const storageRef = storage.ref().child(`${folder}/${file.name}`);
    const uploadTask = await storageRef.put(file);
    return uploadTask.ref.getDownloadURL();
  }
  return null;
};

export const createSessionBadge =
  (uid, payload, onSuccess = () => {}) =>
  async (dispatch) => {
    dispatch(updateLoader(true));

    try {
      // Extract QR code file if present for background upload
      const qrCodeFile = payload?.qrCode instanceof File ? payload.qrCode : null;
      
      // Sanitize payload (temporarily remove QR code file object)
      if (qrCodeFile) {
        payload.qrCode = null; // Will be updated in background
      }
      
      const sanitizedPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined && v !== null)
      );

      const sanitizePayload = {
        ...sanitizedPayload,
        managerRating: false,
        isDeleted: false,
      };

      // Create badge document immediately (without waiting for QR upload)
      const docRef = await firebase
        .firestore()
        .collection("seasonal_badges")
        .add({ ...sanitizePayload, bought: 0 });
      const badgeId = docRef.id;

      // Show success immediately, run remaining operations in background
      onSuccess(badgeId);
      toast.success("Badge Added Successfully");
      dispatch(updateLoader(false));

      // Background operations (don't await these - they run asynchronously)
      const backgroundTasks = [
        // Update user stats
        firebase
          .firestore()
          .collection("users")
          .doc(uid)
          .update({
            seasonalBadge: firebase.firestore.FieldValue.increment(1),
            totalSeasonalBadges: firebase.firestore.FieldValue.increment(1),
          }),
        
        // Create notification
        firebase
          .firestore()
          .collection("fan_notifications")
          .add({
            createdAt: firebase.firestore.Timestamp.now(),
            athleteId: uid,
            isRead: [],
            type: "seasonal_badge",
          }),
      ];

      // Add QR code upload to background tasks if present
      if (qrCodeFile) {
        backgroundTasks.push(
          uploadFile(qrCodeFile, "seasonQRcodes")
            .then((mediaDownloadURL) => {
              if (mediaDownloadURL) {
                return firebase
                  .firestore()
                  .collection("seasonal_badges")
                  .doc(badgeId)
                  .update({ qrCode: mediaDownloadURL });
              }
            })
            .catch((error) => console.error("QR upload failed:", error))
        );
      }
      
      // Execute all background tasks in parallel
      Promise.all(backgroundTasks)
        .then(() => {
          // Refresh auth after background tasks complete
          dispatch(refreshAuth(uid));
        })
        .catch((error) => {
          console.error("Background task error:", error);
        });

    } catch (error) {
      console.error("createSessionBadge error:", error);
      dispatch(updateLoader(false));
      toast.error("Error adding badge");
    }
  };

export const updateSeasonalBadge =
  (ID, payload, removedImg, onSuccess = () => {}) =>
  async (dispatch) => {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        toast.error("User not logged in");
        return;
      }
      const docRef = firebase.firestore().collection("seasonal_badges").doc(ID);
      const doc = await docRef.get();
      if (!doc.exists) {
        toast.error("Badge not found");
        return;
      }
      if (doc.data().athleteID !== user.uid) {
        toast.error("You are not authorized to update this badge");
        return;
      }

      dispatch(updateLoader(true));
      if (removedImg) {
        await storage.refFromURL(removedImg).delete();
      }
      if (payload.qrCode instanceof File) {
        const mediaDownloadURL = await uploadFile(
          payload?.qrCode,
          "seasonQRcodes"
        );
        payload.qrCode = await mediaDownloadURL;
      }
      await docRef
        .set({ ...payload })
        .then(() => {
          toast.success("Badge Updated Successfully");
          onSuccess();
        });
      dispatch(updateLoader(false));
    } catch (error) {
      console.error(error);
      dispatch(updateLoader(false));
    }
  };

export const getSeasonalBadges =
  (ID, lastDocument, onSuccess) => async (dispatch) => {
    try {
      let querya = firebase
        .firestore()
        .collection("seasonal_badges")
        .where("athleteID", "==", ID)
        .where("isDeleted", "==", false);
      if (lastDocument !== "") {
        querya = querya.startAfter(lastDocument);
      }
      querya = querya.limit(5);
      const unsubscribe = querya.onSnapshot(
        (querySnapshot) => {
          let temp_data = [];
          querySnapshot.forEach((doc) => {
            temp_data.push({
              ...doc.data(),
              id: doc.id,
            });
          });
          dispatch({
            type: lastDocument ? "ATHLETE_BADGES_MORE" : "ATHLETE_BADGES",
            payload: {
              data: temp_data,
              lastDocument: querySnapshot.docs[temp_data.length - 1],
              hasMoreDocument: temp_data.length >= 5,
            },
          });
          onSuccess();
        },
        (error) => {
          dispatch(seasonLoader(false));
          toast.error(error.message);
        }
      );
      return unsubscribe;
    } catch (error) {
      dispatch(seasonLoader(false));
      toast.error(error.message);
    }
  };

export const getSeasonalFootballBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "FOOTBALL")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_FOOTBALL_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalBasketballBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "BASKETBALL")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_BASKETBALL_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalBaseballBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "BASEBALL")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_BASEBALL_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const deleteBadge =
  (id, ID, onComplete = () => {}) =>
  async (dispatch) => {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        toast.error("User not logged in");
        onComplete();
        return;
      }
      const docRef = firebase.firestore().collection("seasonal_badges").doc(ID);
      const doc = await docRef.get();
      if (!doc.exists) {
        toast.error("Badge not found");
        onComplete();
        return;
      }
      if (doc.data().athleteID !== id && doc.data().athleteID !== user.uid) {
        toast.error("You are not authorized to delete this badge");
        onComplete();
        return;
      }

      await docRef.update({
        isDeleted: true,
      });
      onComplete();
    } catch (error) {
      console.error("Error updating data: ", error);
    }
  };

export const isManagerExist =
  (email, onComplete = () => {}) =>
  async (dispatch) => {
    try {
      firebase
        .firestore()
        .collection("users")
        .where("email", "==", email)
        .get()
        .then(async (query) => {
          if (query?.docs[0]?.data().role == "fan") {
            onComplete();
          } else {
            toast.error("please enter valid manager email");
          }
        });
    } catch (error) {
      console.error(error);
    }
  };

export const seasonLoader = (val) => async (dispatch) => {
  dispatch({
    type: "Loading",
    payload: val,
  });
};
export const updateLoader = (val) => async (dispatch) => {
  dispatch({
    type: "UPDATE_LOADING",
    payload: val,
  });
};
export const updateRatingData =
  (ratingData, docId, onComplete = () => {}) =>
  async (dispatch) => {
    try {
      await firebase
        .firestore()
        .collection("seasonal_badges")
        .doc(docId)
        .update(ratingData);
      dispatch({
        type: "ADD_SEASONAL_RATING",
        payload: docId,
      });

      onComplete();
    } catch (error) {
      console.error("Error updating rating data: ", error);
    }
  };
export const addStrategyRating = (data) => () => {
  firebase.firestore().collection("strategy-rating").add(data);
};

export const getSeasonalSoccerBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "SOCCER")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_SOCCER_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalVolleyballBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "VOLLEYBALL")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_VOLLEYBALL_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalTrackandFieldBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "TRACK AND FIELD")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_TRACKANDFIELD_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalCrossCountryBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "CROSS COUNTRY")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_CROSSCOUNTRY_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalTennisBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "TENNIS")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_TENNIS_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalWrestlingBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "WRESTLING")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_WRESTLING_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalSoftballBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "SOFTBALL")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_SOFTBALL_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalSwimmingDivingBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "SWIMMING & DIVING")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_SWIMMINGDIVING_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalGolfBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "GOLF")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_GOLF_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalCompetitiveCheerSpiritBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "COMPETITIVE CHEER/SPIRIT")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_COMPETITIVECHEERSPIRIT_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalLacrosseBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "LACROSSE")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_LACROSSE_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalFlagFootballBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "FLAG FOOTBALL")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_FLAGFOOTBALL_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalBowlingBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "BOWLING")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_BOWLING_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalFieldHockeyBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "FIELD HOCKEY")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_FIELDHOCKEY_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalWaterPoloBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "WATER POLO")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_WATERPOLO_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalIceHockeyBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "ICE HOCKEY")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_ICEHOCKEY_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalWeightliftingBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "WEIGHTLIFTING")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_WEIGHTLIFTING_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalGymnasticsBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "GYMNASTICS")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_GYMNASTICS_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalBeachVolleyballBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "BEACH VOLLEYBALL")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_BEACHVOLLEYBALL_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalSkiingAlpineSkiingBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "SKIING / ALPINE SKIING")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_SKIINGALPINESKIING_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalBadmintonBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "BADMINTON")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_BADMINTON_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalEsportsBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "ESPORTS")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_ESPORTS_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalRugbyBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "RUGBY")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_RUGBY_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalArcheryBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "ARCHERY")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_ARCHERY_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalFishingBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "FISHING")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_FISHING_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalFencingBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "FENCING")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_FENCING_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};

export const getSeasonalOtherBadges = (ID) => async (dispatch) => {
  try {
    dispatch(seasonLoader(true));
    await firebase
      .firestore()
      .collection("seasonal_badges")
      .where("athleteID", "==", ID)
      .where("sport", "==", "OTHER")
      .onSnapshot((query) => {
        let athleteBadges = [];
        for (let doc of query.docs) {
          if (doc.exists) {
            athleteBadges.push({ id: doc.id, ...doc.data() });
          }
        }
        dispatch({
          type: "ATHLETE_OTHER_BADGES",
          payload: athleteBadges,
        });
        dispatch(seasonLoader(false));
      });
  } catch (error) {
    dispatch(seasonLoader(false));
    toast.error(error);
  }
};
